/*
 * OwnerAdvancesPanel — Phase 920.3
 * Isolated master-detail interface for managing Owner Advances.
 * Mounted as a new tab inside BuildingDetails.js. Zero coupling to
 * existing accounting UI.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, HandCoins, FilePdf,
  FileXls, MagnifyingGlass, Circle, Warning, ArrowClockwise,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const T = {
  el: {
    ownerAdvances: 'Χρηματοδοτήσεις Ιδιοκτητών',
    subtitle: 'Χρήματα που το κτίριο οφείλει σε ιδιοκτήτες που πλήρωσαν κοινές δαπάνες',
    // Cards
    totalAdvances: 'Σύνολο Χρηματοδοτήσεων',
    totalOutstanding: 'Σύνολο Υπολοίπου',
    totalReimbursed: 'Σύνολο Επιστροφών',
    activeCount: 'Ενεργές Χρηματοδοτήσεις',
    // Filters
    search: 'Αναζήτηση…',
    apartmentFilter: 'Διαμέρισμα',
    statusFilter: 'Κατάσταση',
    dateFilter: 'Ημερομηνία',
    outstandingOnly: 'Μόνο με Υπόλοιπο',
    linkedExpense: 'Συνδεδεμένη Δαπάνη',
    all: 'Όλα',
    status_open: 'Ανοιχτή',
    status_paid: 'Εξοφλημένη',
    // Table columns
    apartment: 'Διαμ.',
    description: 'Περιγραφή',
    originalAmount: 'Αρχικό Ποσό',
    outstanding: 'Υπόλοιπο',
    status: 'Κατάσταση',
    date: 'Ημερομηνία',
    // Actions
    newAdvance: 'Νέα Χρηματοδότηση',
    edit: 'Επεξεργασία',
    delete: 'Διαγραφή',
    recordReimbursement: 'Καταχώρηση Πληρωμής',
    exportPdf: 'Εξαγωγή PDF',
    exportExcel: 'Εξαγωγή Excel',
    close: 'Κλείσιμο',
    save: 'Αποθήκευση',
    cancel: 'Ακύρωση',
    // Detail tabs
    tabOverview: 'Επισκόπηση',
    tabAllocations: 'Κατανομές',
    tabReimbursements: 'Επιστροφές',
    tabAudit: 'Ιστορικό',
    // Overview panels
    linkedExpenseLabel: 'Συνδεδεμένη Δαπάνη',
    none: '—',
    direct: 'Άμεσες',
    bank: 'Τραπεζικές',
    cash: 'Ταμείο',
    progressLabel: 'Πρόοδος Επιστροφής',
    // Allocations
    allocApt: 'Διαμέρισμα',
    allocAmount: 'Κατανομή',
    allocPaid: 'Πληρώθηκε',
    allocRemaining: 'Υπόλοιπο',
    editAllocations: 'Επεξεργασία Κατανομών',
    autoRecalculate: 'Αυτόματος Υπολογισμός',
    allocationsLocked: 'Οι κατανομές κλείδωσαν — υπάρχουν επιστροφές',
    // Reimbursements
    method: 'Μέθοδος',
    reference: 'Αναφορά',
    notes: 'Σημειώσεις',
    runningBalance: 'Τρέχον Υπόλοιπο',
    noReimbursements: 'Καμία επιστροφή ακόμα',
    sourcePayment: 'Πηγή Πληρωμής',
    // Phase 920.8 — ledger columns
    ledgerColPaymentDate: 'Ημ/νία Πληρωμής',
    ledgerColApt: 'Διαμ.',
    ledgerColOwner: 'Ιδιοκτήτης',
    ledgerColInvoiceNo: 'Αρ. Τιμ.',
    ledgerColPaymentNo: 'Αρ. Πληρωμής',
    ledgerColMethod: 'Μέθοδος',
    ledgerColReference: 'Αναφορά',
    ledgerColAmount: 'Ποσό',
    detailsTitle: 'Λεπτομέρειες Επιστροφής',
    method_direct: 'Άμεση',
    method_bank: 'Τράπεζα',
    method_cash: 'Ταμείο',
    method_invoice: 'Τιμολόγιο',
    // Audit
    auditAt: 'Στιγμιότυπο',
    auditBy: 'Χρήστης',
    auditAction: 'Ενέργεια',
    auditDetails: 'Λεπτομέρειες',
    // Forms
    ownerApt: 'Ιδιοκτήτης (Διαμέρισμα)',
    amount: 'Ποσό (€)',
    descriptionField: 'Περιγραφή',
    fromExpense: 'Από Υπάρχουσα Δαπάνη',
    manualEntry: 'Χειροκίνητη Καταχώρηση',
    alreadyContributed: 'Ήδη Καταβεβλημένη',
    reimburseAction: 'Επιστροφή',
    directMethod: 'Τρόπος Άμεσης Πληρωμής',
    directCash: 'Μετρητά',
    directBankTransfer: 'Τραπεζικό Έμβασμα',
    method_direct_cash: 'Μετρητά (Άμεση)',
    method_direct_bank_transfer: 'Τραπ. Έμβασμα (Άμεση)',
    // Empty & messages
    noAdvances: 'Δεν υπάρχουν Χρηματοδοτήσεις Ιδιοκτητών ακόμα',
    selectAdvance: 'Επιλέξτε μια Χρηματοδότηση από τη λίστα',
    confirmDelete: 'Επιβεβαίωση Διαγραφής',
    confirmDeleteMsg: 'Θέλετε σίγουρα να διαγράψετε αυτή τη Χρηματοδότηση; Η ενέργεια είναι μαλακή (soft-delete).',
    confirmDeleteReimbMsg: 'Διαγραφή αυτής της επιστροφής; Η σχετική συναλλαγή Τράπεζας/Ταμείου και οι συνδεδεμένες κατανομές θα ακυρωθούν και τα υπόλοιπα θα επανυπολογιστούν.',
    // Phase 920.11 — Owner Advance Payment Destination + Method
    paymentDestination: 'Προορισμός Πληρωμής Χρηματοδότησης',
    destBuildingBank: 'Λογαριασμός Πολυκατοικίας',
    destDirect: 'Απευθείας στον Ιδιοκτήτη της Χρηματοδότησης',
    paymentMethod: 'Μέθοδος Πληρωμής',
    pmBank: 'Τράπεζα',
    pmCash: 'Μετρητά',
    // Phase 920.13 — Repair Invoice selector.
    repairInvoice: 'Τιμολόγιο Επισκευής',
    repairInvoicePlaceholder: 'Επιλέξτε τιμολόγιο',
    noRepairInvoice: '— Χωρίς σύνδεση —',
    // Phase 920.14 — Statement visibility toggle.
    showOnStatement: 'Εμφάνιση στην Κατάσταση Ιδιοκτήτη',
    showOnStatementHint: 'Δείχνει τη Cash/Bank εισπραξη ως πίστωση στην κατάσταση του advancing owner. Απενεργοποίησέ το για εσωτερική ταμειακή προσαρμογή.',
    // Phase 920.15 — Edit Reimbursement dialog.
    editReimbTitle: 'Επεξεργασία Επιστροφής',
    payerAptLocked: 'Πληρωτής Διαμέρισμα (κλειδωμένο)',
    methodLocked: 'Μέθοδος (κλειδωμένη)',
    editReimbAmountBankCashHint: 'Η επεξεργασία ποσού θα ενημερώσει επίσης τη γονική Πληρωμή Διαμερίσματος και το ταμείο.',
    editReimbSaved: 'Αποθηκεύτηκε',
    editReimbAction: 'Επεξεργασία',
  },
  en: {
    ownerAdvances: 'Owner Advances',
    subtitle: 'Money the building owes to owners who paid common expenses on its behalf',
    totalAdvances: 'Total Advances',
    totalOutstanding: 'Total Outstanding',
    totalReimbursed: 'Total Reimbursed',
    activeCount: 'Active Advances',
    search: 'Search…',
    apartmentFilter: 'Apartment',
    statusFilter: 'Status',
    dateFilter: 'Date',
    outstandingOnly: 'Outstanding only',
    linkedExpense: 'Linked expense',
    all: 'All',
    status_open: 'Open',
    status_paid: 'Paid',
    apartment: 'Apt',
    description: 'Description',
    originalAmount: 'Original',
    outstanding: 'Outstanding',
    status: 'Status',
    date: 'Date',
    newAdvance: 'New Owner Advance',
    edit: 'Edit',
    delete: 'Delete',
    recordReimbursement: 'Record Payment',
    exportPdf: 'Export PDF',
    exportExcel: 'Export Excel',
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    tabOverview: 'Overview',
    tabAllocations: 'Allocations',
    tabReimbursements: 'Reimbursements',
    tabAudit: 'Audit Log',
    linkedExpenseLabel: 'Linked Expense',
    none: '—',
    direct: 'Direct',
    bank: 'Bank',
    cash: 'Cash',
    progressLabel: 'Reimbursement progress',
    allocApt: 'Apartment',
    allocAmount: 'Allocated',
    allocPaid: 'Paid',
    allocRemaining: 'Remaining',
    editAllocations: 'Edit Allocations',
    autoRecalculate: 'Auto-recalculate',
    allocationsLocked: 'Allocations locked — reimbursements exist',
    method: 'Method',
    reference: 'Reference',
    notes: 'Notes',
    runningBalance: 'Running Balance',
    noReimbursements: 'No reimbursements yet',
    sourcePayment: 'Source Payment',
    ledgerColPaymentDate: 'Payment Date',
    ledgerColApt: 'Apt',
    ledgerColOwner: 'Owner',
    ledgerColInvoiceNo: 'Invoice #',
    ledgerColPaymentNo: 'Payment #',
    ledgerColMethod: 'Method',
    ledgerColReference: 'Reference',
    ledgerColAmount: 'Amount',
    detailsTitle: 'Reimbursement Details',
    method_direct: 'Direct',
    method_bank: 'Bank',
    method_cash: 'Cash',
    method_invoice: 'Invoice',
    auditAt: 'Timestamp',
    auditBy: 'User',
    auditAction: 'Action',
    auditDetails: 'Details',
    ownerApt: 'Owner (Apartment)',
    amount: 'Amount (€)',
    descriptionField: 'Description',
    fromExpense: 'From existing Expense',
    manualEntry: 'Manual Entry',
    directReimbFormTitle: 'Record Direct Reimbursement',
    payerApt: 'Payer Apartment',
    alreadyContributed: 'Already Contributed',
    reimburseAction: 'Reimburse',
    directMethod: 'Direct Method',
    directCash: 'Direct Cash',
    directBankTransfer: 'Direct Bank Transfer',
    method_direct_cash: 'Direct Cash',
    method_direct_bank_transfer: 'Direct Bank Transfer',
    noAdvances: 'No Owner Advances yet',
    selectAdvance: 'Select an advance from the list',
    confirmDelete: 'Confirm Delete',
    confirmDeleteMsg: 'Are you sure you want to delete this Owner Advance? This is a soft-delete.',
    confirmDeleteReimbMsg: 'Delete this reimbursement? The linked Bank/Cash transaction and any allocations will be reversed and balances recalculated.',
    // Phase 920.11 — Owner Advance Payment Destination + Method
    paymentDestination: 'Owner Advance Payment Destination',
    destBuildingBank: 'Building Account',
    destDirect: 'Paid Directly to the Advancing Owner',
    paymentMethod: 'Payment Method',
    pmBank: 'Bank',
    pmCash: 'Cash',
    // Phase 920.13 — Repair Invoice selector.
    repairInvoice: 'Repair Invoice',
    repairInvoicePlaceholder: 'Select invoice',
    noRepairInvoice: '— Not linked —',
    // Phase 920.14 — Statement visibility toggle.
    showOnStatement: 'Show on Owner Statement',
    showOnStatementHint: 'Emit a Statement Credit to the advancing owner. Uncheck for a pure internal cash adjustment.',
    // Phase 920.15 — Edit Reimbursement dialog.
    editReimbTitle: 'Edit Reimbursement',
    payerAptLocked: 'Payer Apartment (locked)',
    methodLocked: 'Method (locked)',
    editReimbAmountBankCashHint: 'Amount edits also resize the parent Apartment Payment and adjust the fund balance.',
    editReimbSaved: 'Saved',
    editReimbAction: 'Edit',
  },
};

export default function OwnerAdvancesPanel({ buildingId, apartments = [], language = 'el', isAdmin = true }) {
  const t = (k) => T[language]?.[k] || T.en[k] || k;

  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const [filters, setFilters] = useState({ q: '', apt: 'all', status: 'all',
                                            date: '', outstandingOnly: false,
                                            linkedOnly: false });

  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceDialogMode, setAdvanceDialogMode] = useState('create'); // create | edit
  const [advanceForm, setAdvanceForm] = useState(null);
  const [reimbDialogOpen, setReimbDialogOpen] = useState(false);
  const [reimbForm, setReimbForm] = useState({
    payer_apartment_id: '',
    amount: '',
    date: '',
    reference: '',
    notes: '',
    destination: 'building',
    method: 'bank',
    // Phase 920.13 — Repair Invoice explicitly picked by the operator.
    linked_invoice_id: '',
    // Phase 920.14 — Whether the reimbursement should appear on the
    // advancing owner's Statement. Defaults to true for bank (money
    // physically received) and false for cash (typical internal
    // adjustment). Operator can override.
    show_on_statement: true,
  });
  // Repair invoices for the currently selected payer / advance.
  const [payerInvoices, setPayerInvoices] = useState([]);
  const [deleteAdvId, setDeleteAdvId] = useState(null);
  const [deleteReimbId, setDeleteReimbId] = useState(null);

  // Phase 920.15 — Edit Reimbursement dialog state.
  const [editReimbOpen, setEditReimbOpen] = useState(false);
  const [editReimbForm, setEditReimbForm] = useState(null);
  const [editReimbSaving, setEditReimbSaving] = useState(false);

  // ---------- Fetch ----------
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/owner-advances`);
      setAdvances(r.data?.advances || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load advances');
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/owner-advances/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load detail');
    }
  }, [buildingId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchDetail(selectedId); }, [selectedId, fetchDetail]);

  // Phase 920.13 — Load the payer's Repair Invoices linked to this
  // Owner Advance so the operator can pick which one is being paid.
  // Refetches whenever either the payer changes or the dialog re-opens.
  useEffect(() => {
    const payer = reimbForm.payer_apartment_id;
    if (!reimbDialogOpen || !payer || !detail) {
      setPayerInvoices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/apartments/${payer}/invoices`);
        const data = resp.data;
        const invs = Array.isArray(data) ? data : (data.invoices || []);
        const linkedRepair = detail.linked_repair_id || detail.linked_expense_id;
        const matches = invs.filter((iv) =>
          !iv.is_deleted
          && iv.invoice_type === 'repair'
          && (iv.linked_advance_id === detail.entry_id
              || (linkedRepair && iv.repair_id === linkedRepair)),
        );
        if (!cancelled) {
          setPayerInvoices(matches);
          // Auto-select when exactly one candidate exists so the operator
          // doesn't have to click for the common single-invoice case.
          if (matches.length === 1 && !reimbForm.linked_invoice_id) {
            setReimbForm((f) => ({ ...f, linked_invoice_id: matches[0].invoice_id }));
          }
        }
      } catch {
        if (!cancelled) setPayerInvoices([]);
      }
    })();
    return () => { cancelled = true; };
  }, [reimbDialogOpen, reimbForm.payer_apartment_id, detail]);

  // ---------- Derived ----------
  const filtered = useMemo(() => {
    return advances.filter((a) => {
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${a.apartment_number} ${a.owner_name} ${a.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.apt !== 'all' && a.apartment_id !== filters.apt) return false;
      const paid = (a.outstanding_balance || 0) <= 0.01;
      if (filters.status === 'open' && paid) return false;
      if (filters.status === 'paid' && !paid) return false;
      if (filters.outstandingOnly && paid) return false;
      if (filters.linkedOnly && !a.linked_expense_id) return false;
      if (filters.date && a.date !== filters.date) return false;
      return true;
    });
  }, [advances, filters]);

  const totals = useMemo(() => {
    return advances.reduce((acc, a) => ({
      total: acc.total + (a.amount || 0),
      outstanding: acc.outstanding + (a.outstanding_balance || 0),
      reimbursed: acc.reimbursed + (a.reimbursed_total || 0),
      active: acc.active + (((a.outstanding_balance || 0) > 0.01) ? 1 : 0),
    }), { total: 0, outstanding: 0, reimbursed: 0, active: 0 });
  }, [advances]);

  // ---------- Actions ----------
  const openCreate = () => {
    setAdvanceDialogMode('create');
    setAdvanceForm({
      apartment_id: '',
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      description: '',
      reference: '',
      notes: '',
      allocations: null,   // null = auto
    });
    setAdvanceDialogOpen(true);
  };
  const openEdit = () => {
    if (!detail) return;
    setAdvanceDialogMode('edit');
    setAdvanceForm({
      entry_id: detail.entry_id,
      apartment_id: detail.apartment_id,
      amount: detail.amount,
      date: detail.date,
      description: detail.description || '',
      reference: detail.reference || '',
      notes: detail.notes || '',
      allocations: detail.allocations || [],
    });
    setAdvanceDialogOpen(true);
  };

  const submitAdvance = async () => {
    if (!advanceForm) return;
    const body = { ...advanceForm };
    if (body.amount) body.amount = Number(body.amount);
    if (body.allocations === null) delete body.allocations;   // let backend auto
    try {
      if (advanceDialogMode === 'create') {
        const r = await axios.post(`${API}/buildings/${buildingId}/owner-advances`, body);
        toast.success(language === 'el' ? 'Δημιουργήθηκε' : 'Created');
        setAdvanceDialogOpen(false);
        await fetchList();
        setSelectedId(r.data?.entry_id);
      } else {
        await axios.put(`${API}/buildings/${buildingId}/owner-advances/${advanceForm.entry_id}`, body);
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
        setAdvanceDialogOpen(false);
        await fetchList();
        await fetchDetail(advanceForm.entry_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const submitDelete = async () => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}/owner-advances/${deleteAdvId}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setDeleteAdvId(null);
      if (selectedId === deleteAdvId) setSelectedId(null);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
      setDeleteAdvId(null);
    }
  };

  const openReimb = (prefill = {}) => {
    setReimbForm({
      payer_apartment_id: prefill.payer_apartment_id || '',
      amount: prefill.amount !== undefined ? String(prefill.amount) : '',
      date: prefill.date || new Date().toISOString().slice(0, 10),
      reference: prefill.reference || '',
      notes: prefill.notes || '',
      destination: prefill.destination || 'building',
      method: prefill.method || 'bank',
      linked_invoice_id: prefill.linked_invoice_id || '',
      // Sensible default: Bank → show on statement, Cash → hide.
      show_on_statement: prefill.show_on_statement !== undefined
        ? !!prefill.show_on_statement
        : ((prefill.method || 'bank') === 'bank'),
    });
    setPayerInvoices([]);
    setReimbDialogOpen(true);
  };
  const submitReimb = async () => {
    if (!detail) return;
    const amt = Number(reimbForm.amount);
    if (!amt || amt <= 0) {
      toast.error(language === 'el' ? 'Μη έγκυρο ποσό' : 'Invalid amount');
      return;
    }
    if (!reimbForm.payer_apartment_id) {
      toast.error(language === 'el' ? 'Επιλέξτε διαμέρισμα' : 'Select an apartment');
      return;
    }
    try {
      if (reimbForm.destination === 'building') {
        // Path A — money is received by the Building. Create an
        // Apartment Payment with payment_source == chosen method so
        // it lands in Bank OR Cash Receipts accordingly, then allocate
        // 100% to this Owner Advance. The backend allocation engine
        // automatically issues a Statement Credit to the advancing owner.
        const payment_source = reimbForm.method === 'bank' ? 'bank' : 'cash';
        const payResp = await axios.post(
          `${API}/apartments/${reimbForm.payer_apartment_id}/payments`,
          {
            apartment_id: reimbForm.payer_apartment_id,
            building_id: buildingId,
            amount: amt,
            date: reimbForm.date,
            payment_source,
            notes: reimbForm.notes || `Owner Advance reimbursement — ${detail.entry_id}`,
          },
        );
        const paymentId = payResp.data?.payment_id;
        if (!paymentId) throw new Error('payment_id missing');
        await axios.post(
          `${API}/apartment-payments/${paymentId}/allocations`,
          {
            allocations: [{
              destination: 'owner_advance',
              target_id: detail.entry_id,
              amount: amt,
              notes: reimbForm.notes,
              reference: reimbForm.reference,
              linked_invoice_id: reimbForm.linked_invoice_id || null,
              show_on_statement: !!reimbForm.show_on_statement,
            }],
          },
        );
      } else {
        // Path B — the debtor apartment paid the advancing owner
        // directly. No Building movement, no Statement Credit. Record
        // how the money changed hands so the audit trail is honest.
        await axios.post(
          `${API}/buildings/${buildingId}/owner-advances/${detail.entry_id}/direct-reimbursements`,
          {
            payer_apartment_id: reimbForm.payer_apartment_id,
            amount: amt,
            date: reimbForm.date,
            direct_sub_method: reimbForm.method === 'bank' ? 'bank_transfer' : 'cash',
            reference: reimbForm.reference,
            notes: reimbForm.notes,
            linked_invoice_id: reimbForm.linked_invoice_id || null,
          },
        );
      }
      toast.success(language === 'el' ? 'Καταχωρήθηκε' : 'Recorded');
      setReimbDialogOpen(false);
      await Promise.all([fetchList(), fetchDetail(detail.entry_id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteReimbursement = async () => {
    if (!detail || !deleteReimbId) return;
    try {
      // Phase 920.16 — Unified delete endpoint works for direct/bank/cash.
      await axios.delete(`${API}/buildings/${buildingId}/owner-advances/${detail.entry_id}/reimbursements/${deleteReimbId}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setDeleteReimbId(null);
      await Promise.all([fetchList(), fetchDetail(detail.entry_id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      setDeleteReimbId(null);
    }
  };

  // Phase 920.15 — Edit Reimbursement handlers.
  const openEditReimb = (r) => {
    if (!r) return;
    setEditReimbForm({
      reimbursement_id: r.reimbursement_id,
      method: r.method,
      payer_apartment_id: r.payer_apartment_id,
      payer_apartment_number: r.payer_apartment_number || '',
      payer_owner_name: r.payer_owner_name || r.payer_name || '',
      amount: String(r.amount ?? ''),
      date: (r.date || '').slice(0, 10),
      reference: r.reference || '',
      notes: r.notes || '',
      linked_invoice_id: r.linked_invoice_id || '',
      direct_sub_method: r.direct_sub_method || 'cash',
      show_on_statement: r.show_on_statement !== false,
      source_payment_id: r.source_payment_id || null,
    });
    setEditReimbOpen(true);
  };

  const submitEditReimb = async () => {
    if (!detail || !editReimbForm) return;
    const amt = Number(editReimbForm.amount);
    if (!amt || amt <= 0) {
      toast.error(language === 'el' ? 'Μη έγκυρο ποσό' : 'Invalid amount');
      return;
    }
    if (!editReimbForm.date) {
      toast.error(language === 'el' ? 'Ημερομηνία απαιτείται' : 'Date required');
      return;
    }
    setEditReimbSaving(true);
    try {
      const body = {
        amount: amt,
        date: editReimbForm.date,
        reference: editReimbForm.reference || '',
        notes: editReimbForm.notes || '',
        linked_invoice_id: editReimbForm.linked_invoice_id || null,
      };
      if (editReimbForm.method === 'direct') {
        body.direct_sub_method = editReimbForm.direct_sub_method || 'cash';
      } else {
        // Phase 920.16 — Method (Bank ↔ Cash) is now editable.
        body.method = editReimbForm.method;
        body.show_on_statement = !!editReimbForm.show_on_statement;
      }
      await axios.put(
        `${API}/buildings/${buildingId}/owner-advances/${detail.entry_id}/reimbursements/${editReimbForm.reimbursement_id}`,
        body,
      );
      toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      setEditReimbOpen(false);
      await Promise.all([fetchList(), fetchDetail(detail.entry_id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setEditReimbSaving(false);
    }
  };

  const exportFile = async (kind) => {
    try {
      const url = `${API}/buildings/${buildingId}/owner-advances/export.${kind}`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `owner_advances_${buildingId}.${kind}`;
      link.click();
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const previewAutoAllocation = async () => {
    if (!advanceForm?.apartment_id || !advanceForm?.amount) return;
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/owner-advances/preview-allocation`, {
        params: { amount: Number(advanceForm.amount), apartment_id: advanceForm.apartment_id },
      });
      setAdvanceForm({ ...advanceForm, allocations: r.data.allocations });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  // Reimbursed-per-apartment for the Allocations tab
  const paidByApt = useMemo(() => {
    if (!detail) return {};
    const map = {};
    for (const r of detail.reimbursements || []) {
      const k = r.payer_apartment_id;
      map[k] = (map[k] || 0) + (r.amount || 0);
    }
    return map;
  }, [detail]);

  // ---------- UI ----------
  const statusChip = (a) => {
    const paid = (a.outstanding_balance || 0) <= 0.01;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${paid ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
        <Circle size={7} weight="fill" />
        {paid ? t('status_paid') : t('status_open')}
      </span>
    );
  };

  const pct = detail ? Math.min(100, Math.round(((detail.reimbursed_total || 0) / Math.max(1, detail.amount || 1)) * 100)) : 0;

  return (
    <div className="space-y-4" data-testid="owner-advances-panel">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('ownerAdvances')}
          </h2>
          <p className="text-xs text-zinc-500 mt-1">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportFile('csv')} className="rounded-none" data-testid="oa-export-csv">
            <FileXls size={14} className="mr-1.5" />{t('exportExcel')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} className="rounded-none" data-testid="oa-export-pdf">
            <FilePdf size={14} className="mr-1.5" />{t('exportPdf')}
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={openCreate} className="rounded-none bg-zinc-950" data-testid="oa-new-btn">
              <Plus size={14} className="mr-1.5" />{t('newAdvance')}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={t('totalAdvances')} value={fmtEur(totals.total)} tone="zinc" testId="oa-kpi-total" />
        <Kpi label={t('totalOutstanding')} value={fmtEur(totals.outstanding)} tone="amber" testId="oa-kpi-outstanding" />
        <Kpi label={t('totalReimbursed')} value={fmtEur(totals.reimbursed)} tone="emerald" testId="oa-kpi-reimbursed" />
        <Kpi label={t('activeCount')} value={String(totals.active)} tone="blue" testId="oa-kpi-active" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[160px]">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                   placeholder={t('search')} className="rounded-none pl-7" data-testid="oa-search-input" />
          </div>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[10px] uppercase text-zinc-500">{t('apartmentFilter')}</Label>
          <Select value={filters.apt} onValueChange={(v) => setFilters({ ...filters, apt: v })}>
            <SelectTrigger className="rounded-none h-9" data-testid="oa-filter-apt"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              {apartments.map((a) => (
                <SelectItem key={a.apartment_id} value={a.apartment_id}>{a.apartment_number} — {a.owner_name || '—'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[130px]">
          <Label className="text-[10px] uppercase text-zinc-500">{t('statusFilter')}</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="rounded-none h-9" data-testid="oa-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              <SelectItem value="open">{t('status_open')}</SelectItem>
              <SelectItem value="paid">{t('status_paid')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-zinc-500">{t('dateFilter')}</Label>
          <Input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                 className="rounded-none h-9 w-40" data-testid="oa-filter-date" />
        </div>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={filters.outstandingOnly}
                 onChange={(e) => setFilters({ ...filters, outstandingOnly: e.target.checked })}
                 data-testid="oa-filter-outstanding" />
          {t('outstandingOnly')}
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={filters.linkedOnly}
                 onChange={(e) => setFilters({ ...filters, linkedOnly: e.target.checked })}
                 data-testid="oa-filter-linked" />
          {t('linkedExpense')}
        </label>
        <Button variant="ghost" size="sm" onClick={fetchList} className="rounded-none" data-testid="oa-refresh">
          <ArrowClockwise size={14} />
        </Button>
      </div>

      {/* Master-detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-4">
        {/* List */}
        <div className="bg-white border border-zinc-200">
          <table className="w-full text-sm" data-testid="oa-list-table">
            <thead>
              <tr className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left px-3 py-2 w-14">{t('apartment')}</th>
                <th className="text-left px-3 py-2">{t('description')}</th>
                <th className="text-right px-3 py-2">{t('originalAmount')}</th>
                <th className="text-right px-3 py-2">{t('outstanding')}</th>
                <th className="text-center px-3 py-2">{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="text-center p-6 text-zinc-400 text-xs">…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center p-6 text-zinc-400 text-xs">{t('noAdvances')}</td></tr>
              )}
              {filtered.map((a) => (
                <tr
                  key={a.entry_id}
                  onClick={() => setSelectedId(a.entry_id)}
                  className={`cursor-pointer border-t border-zinc-100 hover:bg-zinc-50 ${selectedId === a.entry_id ? 'bg-blue-50/60' : ''}`}
                  data-testid={`oa-row-${a.entry_id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{a.apartment_number}</td>
                  <td className="px-3 py-2">
                    <div className="text-[13px] font-medium text-zinc-800 line-clamp-1">{a.description || '—'}</div>
                    <div className="text-[10.5px] text-zinc-500">{fmtDate(a.date)} · {a.owner_name || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtEur(a.amount)}</td>
                  <td className={`px-3 py-2 text-right font-mono text-xs font-semibold ${(a.outstanding_balance || 0) <= 0.01 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {fmtEur(a.outstanding_balance)}
                  </td>
                  <td className="px-3 py-2 text-center">{statusChip(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        <div className="bg-white border border-zinc-200 min-h-[420px]" data-testid="oa-detail-panel">
          {!detail ? (
            <div className="h-full flex items-center justify-center text-zinc-400 text-sm p-10">
              {t('selectAdvance')}
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Detail header */}
              <div className="flex items-start justify-between border-b border-zinc-100 pb-3">
                <div>
                  <div className="text-[11px] uppercase text-zinc-500 tracking-wider">
                    {t('apartment')} {detail.apartment_number} · {detail.owner_name || '—'} · {fmtDate(detail.date)}
                  </div>
                  <div className="text-lg font-semibold text-zinc-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {detail.description}
                  </div>
                  {detail.linked_expense_id && (
                    <div className="text-[11px] text-blue-700 mt-1">
                      {t('linkedExpenseLabel')}: <span className="font-mono">{detail.linked_expense_id}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="sm" onClick={openReimb} className="rounded-none border-emerald-300 text-emerald-800" data-testid="oa-record-reimb-btn">
                        <HandCoins size={14} className="mr-1.5" />{t('recordReimbursement')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={openEdit} className="rounded-none" data-testid="oa-edit-btn">
                        <PencilSimple size={14} className="mr-1.5" />{t('edit')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteAdvId(detail.entry_id)}
                              className="rounded-none border-red-300 text-red-700" data-testid="oa-delete-btn">
                        <Trash size={14} className="mr-1.5" />{t('delete')}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Money grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label={t('originalAmount')} val={fmtEur(detail.amount)} tone="zinc" />
                <Stat label={t('direct')} val={fmtEur(detail.reimbursed_direct)} tone="blue" />
                <Stat label={t('bank')} val={fmtEur(detail.reimbursed_bank)} tone="blue" />
                <Stat label={t('cash')} val={fmtEur(detail.reimbursed_cash)} tone="blue" />
                <Stat label={t('outstanding')} val={fmtEur(detail.outstanding_balance)} tone="amber" bold />
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
                  <span>{t('progressLabel')}</span>
                  <span className="font-mono">{pct}%</span>
                </div>
                <div className="h-2 bg-zinc-100 border border-zinc-200 relative">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} data-testid="oa-progress-bar" />
                </div>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start rounded-none bg-zinc-50 border border-zinc-200 h-9">
                  <TabsTrigger value="overview" className="rounded-none" data-testid="oa-tab-overview">{t('tabOverview')}</TabsTrigger>
                  <TabsTrigger value="allocations" className="rounded-none" data-testid="oa-tab-allocations">{t('tabAllocations')}</TabsTrigger>
                  <TabsTrigger value="reimbursements" className="rounded-none" data-testid="oa-tab-reimbursements">{t('tabReimbursements')}</TabsTrigger>
                  <TabsTrigger value="audit" className="rounded-none" data-testid="oa-tab-audit">{t('tabAudit')}</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-3 space-y-2 text-sm">
                  <div><span className="text-zinc-500">{t('reference')}: </span>{detail.reference || t('none')}</div>
                  <div><span className="text-zinc-500">{t('notes')}: </span>{detail.notes || t('none')}</div>
                  <div><span className="text-zinc-500">{t('linkedExpenseLabel')}: </span>{detail.linked_expense_id || t('none')}</div>
                </TabsContent>

                <TabsContent value="allocations" className="pt-3">
                  <AllocationsTab
                    detail={detail} paidByApt={paidByApt} t={t}
                    canEdit={isAdmin && (detail.reimbursements || []).length === 0}
                    isAdmin={isAdmin}
                    onReimburse={(payerAptId, remaining) => openReimb({
                      payer_apartment_id: payerAptId,
                      amount: remaining,
                    })}
                  />
                </TabsContent>

                <TabsContent value="reimbursements" className="pt-3">
                  <ReimbursementsTab detail={detail} t={t}
                                     onDelete={(rid) => setDeleteReimbId(rid)}
                                     onEdit={openEditReimb}
                                     isAdmin={isAdmin} />
                </TabsContent>

                <TabsContent value="audit" className="pt-3">
                  <AuditTab detail={detail} t={t} />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Advance Create/Edit Dialog ---------- */}
      {advanceForm && (
        <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
          <DialogContent className="max-w-2xl rounded-none">
            <DialogHeader>
              <DialogTitle>{advanceDialogMode === 'create' ? t('newAdvance') : t('edit')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">{t('ownerApt')}</Label>
                  <Select value={advanceForm.apartment_id} onValueChange={(v) => setAdvanceForm({ ...advanceForm, apartment_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="oa-form-apt"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {apartments.map((a) => (
                        <SelectItem key={a.apartment_id} value={a.apartment_id}>
                          {a.apartment_number} — {a.owner_name || '—'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">{t('date')}</Label>
                  <Input type="date" value={advanceForm.date} onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                         className="rounded-none" data-testid="oa-form-date" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">{t('amount')}</Label>
                  <Input type="number" step="0.01" value={advanceForm.amount}
                         onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                         className="rounded-none font-mono" data-testid="oa-form-amount" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">{t('reference')}</Label>
                  <Input value={advanceForm.reference} onChange={(e) => setAdvanceForm({ ...advanceForm, reference: e.target.value })}
                         className="rounded-none" data-testid="oa-form-reference" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">{t('descriptionField')}</Label>
                <Input value={advanceForm.description} onChange={(e) => setAdvanceForm({ ...advanceForm, description: e.target.value })}
                       className="rounded-none" data-testid="oa-form-description" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">{t('notes')}</Label>
                <Textarea value={advanceForm.notes} rows={2} onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                          className="rounded-none" data-testid="oa-form-notes" />
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[10px] uppercase text-zinc-500">{t('editAllocations')}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={previewAutoAllocation}
                          className="rounded-none text-xs" data-testid="oa-form-auto-alloc"
                          disabled={!advanceForm.apartment_id || !advanceForm.amount}>
                    <ArrowClockwise size={12} className="mr-1" />{t('autoRecalculate')}
                  </Button>
                </div>
                <div className="border border-zinc-200 max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
                      <tr>
                        <th className="text-left px-2 py-1">{t('allocApt')}</th>
                        <th className="text-right px-2 py-1">%</th>
                        <th className="text-right px-2 py-1">€</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(advanceForm.allocations || []).map((r, idx) => (
                        <tr key={r.apartment_id} className="border-t border-zinc-100">
                          <td className="px-2 py-1 font-mono">{r.apartment_number}</td>
                          <td className="px-2 py-1 text-right font-mono">{(r.share_percent || 0).toFixed(2)}%</td>
                          <td className="px-2 py-1 text-right">
                            <Input type="number" step="0.01" value={r.share_amount}
                                   onChange={(e) => {
                                     const next = [...(advanceForm.allocations || [])];
                                     next[idx] = { ...next[idx], share_amount: Number(e.target.value) };
                                     setAdvanceForm({ ...advanceForm, allocations: next });
                                   }}
                                   className="rounded-none h-7 text-right font-mono w-24 ml-auto"
                                   data-testid={`oa-alloc-input-${r.apartment_number}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {(advanceForm.allocations || []).length > 0 && (
                      <tfoot>
                        <tr className="bg-zinc-50 border-t border-zinc-200">
                          <td className="px-2 py-1 font-semibold" colSpan={2}>Σύνολο / Total</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold">
                            {fmtEur((advanceForm.allocations || []).reduce((s, r) => s + Number(r.share_amount || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {(advanceForm.allocations || []).length === 0 && (
                  <p className="text-[11px] text-zinc-500 mt-2 italic">
                    {language === 'el' ? 'Θα υπολογιστούν αυτόματα από το backing shares.' : 'Will be auto-computed from building shares.'}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={submitAdvance} className="rounded-none bg-zinc-950" data-testid="oa-form-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ---------- Reimbursement Dialog (Building Bank OR Direct) ---------- */}
      <Dialog open={reimbDialogOpen} onOpenChange={setReimbDialogOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle>
              {reimbForm.destination === 'direct' ? t('directReimbFormTitle') : t('recordReimbursement')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">{t('payerApt')}</Label>
              <Select value={reimbForm.payer_apartment_id} onValueChange={(v) => setReimbForm({ ...reimbForm, payer_apartment_id: v, linked_invoice_id: '' })}>
                <SelectTrigger className="rounded-none" data-testid="oa-reimb-apt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {apartments.filter((a) => a.apartment_id !== detail?.apartment_id).map((a) => (
                    <SelectItem key={a.apartment_id} value={a.apartment_id}>
                      {a.apartment_number} — {a.owner_name || '—'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase 920.13 — Repair Invoice selector. Empty when no
                matching invoice exists (advance not tied to a repair,
                or invoice already fully paid). */}
            {payerInvoices.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">{t('repairInvoice')}</Label>
                <Select
                  value={reimbForm.linked_invoice_id || '__none__'}
                  onValueChange={(v) => setReimbForm({
                    ...reimbForm,
                    linked_invoice_id: v === '__none__' ? '' : v,
                  })}
                >
                  <SelectTrigger className="rounded-none" data-testid="oa-reimb-invoice">
                    <SelectValue placeholder={t('repairInvoicePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="__none__">{t('noRepairInvoice')}</SelectItem>
                    {payerInvoices.map((iv) => {
                      const remaining = Number(iv.remaining ?? (iv.amount - (iv.paid_amount || 0)));
                      return (
                        <SelectItem key={iv.invoice_id} value={iv.invoice_id}
                                    data-testid={`oa-reimb-invoice-${iv.invoice_id}`}>
                          {(iv.invoice_number || iv.invoice_id)} · €{Number(iv.amount || 0).toFixed(2)}
                          {remaining > 0 ? ` (Rem €${remaining.toFixed(2)})` : ' (paid)'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Phase 920.11 — Owner Advance Payment Destination (the ONLY
                new field added by this phase). */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">{t('paymentDestination')}</Label>
              <Select
                value={reimbForm.destination}
                onValueChange={(v) => setReimbForm({ ...reimbForm, destination: v })}
              >
                <SelectTrigger className="rounded-none" data-testid="oa-reimb-dest">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="building" data-testid="oa-reimb-dest-building">
                    {t('destBuildingBank')}
                  </SelectItem>
                  <SelectItem value="direct" data-testid="oa-reimb-dest-direct">
                    {t('destDirect')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Phase 920.11 — Payment Method (Bank / Cash). Drives
                payment_source for the building path and direct_sub_method
                for the direct path. */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">{t('paymentMethod')}</Label>
              <div className="flex gap-2 mt-1">
                <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border cursor-pointer text-xs ${reimbForm.method === 'bank' ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-zinc-300 text-zinc-600'}`}>
                  <input type="radio" name="reimb-method" value="bank"
                         checked={reimbForm.method === 'bank'}
                         onChange={() => setReimbForm({ ...reimbForm, method: 'bank', show_on_statement: true })}
                         data-testid="oa-reimb-method-bank" />
                  {t('pmBank')}
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border cursor-pointer text-xs ${reimbForm.method === 'cash' ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-zinc-300 text-zinc-600'}`}>
                  <input type="radio" name="reimb-method" value="cash"
                         checked={reimbForm.method === 'cash'}
                         onChange={() => setReimbForm({ ...reimbForm, method: 'cash', show_on_statement: false })}
                         data-testid="oa-reimb-method-cash" />
                  {t('pmCash')}
                </label>
              </div>
            </div>

            {/* Phase 920.14 — Statement visibility. Only relevant when
                money reaches the Building; Direct never emits a Credit. */}
            {reimbForm.destination === 'building' && (
              <label className="flex items-start gap-2 text-xs cursor-pointer"
                     data-testid="oa-reimb-show-statement-wrap">
                <input type="checkbox"
                       className="mt-0.5"
                       checked={!!reimbForm.show_on_statement}
                       onChange={(e) => setReimbForm({ ...reimbForm, show_on_statement: e.target.checked })}
                       data-testid="oa-reimb-show-statement" />
                <div>
                  <div className="font-semibold text-zinc-800">{t('showOnStatement')}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{t('showOnStatementHint')}</div>
                </div>
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">{t('amount')}</Label>
                <Input type="number" step="0.01" value={reimbForm.amount}
                       onChange={(e) => setReimbForm({ ...reimbForm, amount: e.target.value })}
                       className="rounded-none font-mono" data-testid="oa-reimb-amount" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">{t('date')}</Label>
                <Input type="date" value={reimbForm.date}
                       onChange={(e) => setReimbForm({ ...reimbForm, date: e.target.value })}
                       className="rounded-none" data-testid="oa-reimb-date" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">{t('reference')}</Label>
              <Input value={reimbForm.reference}
                     onChange={(e) => setReimbForm({ ...reimbForm, reference: e.target.value })}
                     className="rounded-none" data-testid="oa-reimb-reference" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">{t('notes')}</Label>
              <Textarea value={reimbForm.notes} rows={2}
                        onChange={(e) => setReimbForm({ ...reimbForm, notes: e.target.value })}
                        className="rounded-none" data-testid="oa-reimb-notes" />
            </div>
            {detail && (() => {
              // Phase 920.11 fix — cap is the payer's OWN remaining
              // allocation on this advance, not the global outstanding.
              const payerId = reimbForm.payer_apartment_id;
              const alloc = (detail.allocations || []).find(
                (a) => a.apartment_id === payerId,
              );
              const share = Number(alloc?.share_amount || 0);
              const paid = Number(paidByApt[payerId] || 0);
              const payerRemaining = Math.max(0, share - paid);
              const maxLabel = payerId
                ? fmtEur(payerRemaining)
                : fmtEur(detail.outstanding_balance);
              return (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 flex items-start gap-1.5"
                     data-testid="oa-reimb-max-hint">
                  <Warning size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{language === 'el' ? `Μέγιστο ποσό: ${maxLabel}` : `Max amount: ${maxLabel}`}</span>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReimbDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
            <Button onClick={submitReimb} className="rounded-none bg-emerald-700 hover:bg-emerald-800" data-testid="oa-reimb-save">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Edit Reimbursement Dialog (Phase 920.15) ---------- */}
      <Dialog open={editReimbOpen} onOpenChange={setEditReimbOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="oa-reimb-edit-dialog">
          <DialogHeader>
            <DialogTitle>{t('editReimbTitle')}</DialogTitle>
          </DialogHeader>
          {editReimbForm && (
            <div className="space-y-3">
              {/* Locked payer + method label (method editable below for bank/cash) */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-zinc-50 border border-zinc-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    {t('payerAptLocked')}
                  </div>
                  <div className="mt-1 font-mono">
                    {editReimbForm.payer_apartment_number}
                    {editReimbForm.payer_owner_name ? ` — ${editReimbForm.payer_owner_name}` : ''}
                  </div>
                </div>
                {editReimbForm.method === 'direct' ? (
                  <div className="bg-zinc-50 border border-zinc-200 p-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {t('methodLocked')}
                    </div>
                    <div className="mt-1 font-mono">{t('method_direct')}</div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">{t('paymentMethod')}</Label>
                    <Select
                      value={editReimbForm.method}
                      onValueChange={(v) => setEditReimbForm((f) => ({ ...f, method: v }))}
                    >
                      <SelectTrigger className="rounded-none h-9" data-testid="oa-reimb-edit-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">{t('pmBank')}</SelectItem>
                        <SelectItem value="cash">{t('pmCash')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">{t('amount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editReimbForm.amount}
                  onChange={(e) => setEditReimbForm((f) => ({ ...f, amount: e.target.value }))}
                  className="rounded-none"
                  data-testid="oa-reimb-edit-amount"
                />
                {editReimbForm.method !== 'direct' && editReimbForm.source_payment_id && (
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {t('editReimbAmountBankCashHint')}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs">{t('date')}</Label>
                <Input
                  type="date"
                  value={editReimbForm.date}
                  onChange={(e) => setEditReimbForm((f) => ({ ...f, date: e.target.value }))}
                  className="rounded-none"
                  data-testid="oa-reimb-edit-date"
                />
              </div>

              {editReimbForm.method === 'direct' && (
                <div>
                  <Label className="text-xs">{t('directMethod')}</Label>
                  <Select
                    value={editReimbForm.direct_sub_method || 'cash'}
                    onValueChange={(v) => setEditReimbForm((f) => ({ ...f, direct_sub_method: v }))}
                  >
                    <SelectTrigger className="rounded-none" data-testid="oa-reimb-edit-sub-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('directCash')}</SelectItem>
                      <SelectItem value="bank_transfer">{t('directBankTransfer')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {editReimbForm.method !== 'direct' && (
                <div className="flex items-start gap-2 border border-zinc-200 p-2 bg-zinc-50">
                  <input
                    type="checkbox"
                    id="oa-reimb-edit-show"
                    checked={!!editReimbForm.show_on_statement}
                    onChange={(e) => setEditReimbForm((f) => ({ ...f, show_on_statement: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="oa-reimb-edit-show-on-statement"
                  />
                  <label htmlFor="oa-reimb-edit-show" className="text-xs">
                    <div className="font-medium text-zinc-800">{t('showOnStatement')}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{t('showOnStatementHint')}</div>
                  </label>
                </div>
              )}

              <div>
                <Label className="text-xs">{t('reference')}</Label>
                <Input
                  value={editReimbForm.reference || ''}
                  onChange={(e) => setEditReimbForm((f) => ({ ...f, reference: e.target.value }))}
                  className="rounded-none"
                  data-testid="oa-reimb-edit-reference"
                />
              </div>

              <div>
                <Label className="text-xs">{t('notes')}</Label>
                <Textarea
                  rows={2}
                  value={editReimbForm.notes || ''}
                  onChange={(e) => setEditReimbForm((f) => ({ ...f, notes: e.target.value }))}
                  className="rounded-none"
                  data-testid="oa-reimb-edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditReimbOpen(false)} className="rounded-none">
              {t('cancel')}
            </Button>
            <Button
              onClick={submitEditReimb}
              disabled={editReimbSaving}
              className="rounded-none bg-blue-700 hover:bg-blue-800"
              data-testid="oa-reimb-edit-save"
            >
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Delete Advance Confirm ---------- */}
      <AlertDialog open={!!deleteAdvId} onOpenChange={(v) => { if (!v) setDeleteAdvId(null); }}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteMsg')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={submitDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="oa-delete-confirm">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Delete Reimbursement Confirm ---------- */}
      <AlertDialog open={!!deleteReimbId} onOpenChange={(v) => { if (!v) setDeleteReimbId(null); }}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteReimbMsg')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteReimbursement} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="oa-reimb-delete-confirm">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Note: allocations editor lives inside the main advance form. A
          dedicated standalone editor may come in a Phase 3.1 backlog item. */}
    </div>
  );
}

/* -------------------- helpers -------------------- */
function Kpi({ label, value, tone = 'zinc', testId }) {
  const toneCls = {
    zinc: 'text-zinc-900',
    amber: 'text-amber-700',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
  }[tone] || 'text-zinc-900';
  return (
    <div className="bg-white border border-zinc-200 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className={`text-2xl font-semibold mt-1 font-mono ${toneCls}`}>{value}</div>
    </div>
  );
}

function Stat({ label, val, tone = 'zinc', bold = false }) {
  const toneCls = {
    zinc: 'text-zinc-900',
    amber: 'text-amber-700',
    blue: 'text-blue-700',
  }[tone] || 'text-zinc-900';
  return (
    <div className="bg-zinc-50 border border-zinc-200 p-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm mt-1 font-mono ${bold ? 'font-bold' : 'font-medium'} ${toneCls}`}>{val}</div>
    </div>
  );
}

function AllocationsTab({ detail, paidByApt, t, canEdit, isAdmin, onReimburse }) {
  const rows = detail?.allocations || [];
  const ownerAptId = detail?.apartment_id;
  if (rows.length === 0) return <p className="text-xs text-zinc-500 italic">—</p>;
  return (
    <div>
      {!canEdit && (
        <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
          {t('allocationsLocked')}
        </div>
      )}
      <div className="border border-zinc-200 max-h-72 overflow-y-auto">
        <table className="w-full text-xs" data-testid="oa-allocations-table">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2">{t('allocApt')}</th>
              <th className="text-right px-3 py-2">%</th>
              <th className="text-right px-3 py-2">{t('allocAmount')}</th>
              <th className="text-right px-3 py-2">{t('allocPaid')}</th>
              <th className="text-right px-3 py-2">{t('allocRemaining')}</th>
              <th className="text-center px-3 py-2">{t('status')}</th>
              <th className="text-center px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOwner = r.apartment_id === ownerAptId;
              const paid = isOwner ? (r.share_amount || 0) : (paidByApt[r.apartment_id] || 0);
              const rem = Math.max(0, (r.share_amount || 0) - paid);
              const done = rem <= 0.01 && (r.share_amount || 0) > 0;
              return (
                <tr key={r.apartment_id} className={`border-t border-zinc-100 ${isOwner ? 'bg-blue-50/40' : ''}`}>
                  <td className="px-3 py-1.5 font-mono">
                    {r.apartment_number}
                    {isOwner && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200"
                            data-testid="oa-alloc-owner-badge">
                        {t('alreadyContributed')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{(r.share_percent || 0).toFixed(2)}%</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtEur(r.share_amount)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmtEur(paid)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono font-semibold ${done ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {fmtEur(rem)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`inline-block px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ${done ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                      {done ? t('status_paid') : t('status_open')}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {/* Phase 920.9 — Per-row direct-reimbursement shortcut. */}
                    {!isOwner && !done && isAdmin && (
                      <Button size="sm" variant="outline"
                              onClick={() => onReimburse && onReimburse(r.apartment_id, rem)}
                              className="rounded-none h-6 px-2 text-[10px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                              data-testid={`oa-alloc-reimburse-${r.apartment_number}`}>
                        <HandCoins size={11} className="mr-1" />{t('reimburseAction')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReimbursementsTab({ detail, t, onDelete, onEdit, isAdmin }) {
  const [selected, setSelected] = React.useState(null);
  const rows = detail?.reimbursements || [];
  if (rows.length === 0) return <p className="text-xs text-zinc-500 italic">{t('noReimbursements')}</p>;
  // Backend already returns rows sorted chronologically ascending. Reverse
  // for newest-first display while computing running balance forward.
  let running = detail.amount || 0;
  const forward = rows.map((r) => { running -= (r.amount || 0); return { ...r, _running: running }; });
  const view = [...forward].reverse();
  const methodClass = (m) => (
    m === 'direct' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
    m === 'bank' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
    m === 'cash' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
    m === 'invoice' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      'bg-zinc-50 text-zinc-700 border border-zinc-200'
  );
  const totalReimbursed = (detail.reimbursed_total || 0);
  const outstanding = (detail.outstanding_balance || 0);
  return (
    <>
      <div className="border border-zinc-200 max-h-96 overflow-y-auto">
        <table className="w-full text-xs" data-testid="oa-reimbursements-table">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">{t('ledgerColPaymentDate')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColApt')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColOwner')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColInvoiceNo')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColPaymentNo')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColMethod')}</th>
              <th className="text-left px-3 py-2">{t('ledgerColReference')}</th>
              <th className="text-right px-3 py-2">{t('ledgerColAmount')}</th>
              <th className="text-right px-3 py-2">{t('runningBalance')}</th>
              <th className="text-center px-3 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {view.map((r) => (
              <tr key={r.reimbursement_id}
                  onClick={() => setSelected(r)}
                  className="border-t border-zinc-100 cursor-pointer hover:bg-blue-50/40"
                  data-testid={`oa-reimb-row-${r.reimbursement_id}`}>
                <td className="px-3 py-1.5 font-mono">{fmtDate(r.date)}</td>
                <td className="px-3 py-1.5 font-mono">{r.payer_apartment_number || '—'}</td>
                <td className="px-3 py-1.5 text-zinc-700">{r.payer_owner_name || '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs">
                  {r.invoice_number
                    ? <span className="px-1 py-0.5 bg-zinc-100 border border-zinc-200 text-zinc-700">{r.invoice_number}</span>
                    : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-zinc-600">
                  {r.source_payment_receipt_number
                    ? <span className="px-1 py-0.5 bg-zinc-100 border border-zinc-200">{r.source_payment_receipt_number}</span>
                    : (r.source_payment_id ? <span className="text-zinc-400">{String(r.source_payment_id).slice(0, 10)}…</span> : <span className="text-zinc-300">—</span>)}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`inline-block px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ${methodClass(r.method)}`}>
                    {t(`method_${r.method}`) || r.method}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-zinc-600">{r.reference || '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtEur(r.amount)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-zinc-500">{fmtEur(r._running)}</td>
                <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    {isAdmin && onEdit && (
                      <button onClick={() => onEdit(r)}
                              className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50"
                              title={t('editReimbAction') || 'Edit'}
                              data-testid={`oa-reimb-edit-${r.reimbursement_id}`}>
                        <PencilSimple size={12} />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => onDelete(r.reimbursement_id)}
                              className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50"
                              title="Delete" data-testid={`oa-reimb-delete-${r.reimbursement_id}`}>
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-zinc-50 border-t-2 border-zinc-200 font-semibold">
            <tr>
              <td className="px-3 py-2 text-right uppercase text-[10px] tracking-wider text-zinc-500" colSpan={7}>{t('totalReimbursed')}</td>
              <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid="oa-reimb-total">{fmtEur(totalReimbursed)}</td>
              <td className="px-3 py-2 text-right font-mono text-amber-700" data-testid="oa-reimb-outstanding">{fmtEur(outstanding)}</td>
              <td></td>
            </tr>
            <tr>
              <td className="px-3 py-1 text-right uppercase text-[10px] tracking-wider text-zinc-400" colSpan={8}>{t('outstanding')}</td>
              <td className="px-3 py-1 text-right font-mono text-amber-700">{fmtEur(outstanding)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Details dialog for the clicked row */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg rounded-none" data-testid="oa-reimb-details-dialog">
          <DialogHeader><DialogTitle>{t('detailsTitle')}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <DetailRow label={t('ledgerColPaymentDate')} value={fmtDate(selected.date)} />
              <DetailRow label={t('ledgerColApt')} value={`${selected.payer_apartment_number || '—'} — ${selected.payer_owner_name || '—'}`} />
              <DetailRow label={t('ledgerColMethod')} value={t(`method_${selected.method}`) || selected.method} />
              <DetailRow label={t('ledgerColAmount')} value={fmtEur(selected.amount)} bold />
              {selected.invoice_number && <DetailRow label={t('ledgerColInvoiceNo')} value={selected.invoice_number} mono />}
              {selected.source_payment_id && (
                <DetailRow label={t('sourcePayment')}
                           value={`${selected.source_payment_receipt_number || ''} · ${selected.source_payment_id}`}
                           mono />
              )}
              {selected.reference && <DetailRow label={t('reference')} value={selected.reference} />}
              {selected.notes && <DetailRow label={t('notes')} value={selected.notes} />}
              {selected.created_by_name && (
                <DetailRow label={t('auditBy')}
                           value={`${selected.created_by_name} @ ${(selected.created_at || '').slice(0, 19).replace('T', ' ')}`} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} className="rounded-none">{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailRow({ label, value, mono = false, bold = false }) {
  return (
    <div className="flex justify-between gap-4 border-b border-zinc-100 pb-1">
      <span className="text-[10.5px] uppercase text-zinc-500 tracking-wider">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-zinc-900' : 'text-zinc-800'}`}>{value}</span>
    </div>
  );
}

function AuditTab({ detail, t }) {
  const events = detail?.audit_log || [];
  if (events.length === 0) return <p className="text-xs text-zinc-500 italic">—</p>;
  const sorted = [...events].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  return (
    <div className="border border-zinc-200 max-h-80 overflow-y-auto">
      <table className="w-full text-xs" data-testid="oa-audit-table">
        <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left px-3 py-2 w-48">{t('auditAt')}</th>
            <th className="text-left px-3 py-2 w-24">{t('auditAction')}</th>
            <th className="text-left px-3 py-2 w-40">{t('auditBy')}</th>
            <th className="text-left px-3 py-2">{t('auditDetails')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e, i) => (
            <tr key={i} className="border-t border-zinc-100">
              <td className="px-3 py-1.5 font-mono text-[11px]">{(e.at || '').slice(0, 19).replace('T', ' ')}</td>
              <td className="px-3 py-1.5">
                <span className={`inline-block px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ${
                  e.action === 'create' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  e.action === 'update' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  e.action === 'delete' ? 'bg-red-50 text-red-700 border border-red-200' :
                                          'bg-zinc-50 text-zinc-700 border border-zinc-200'}`}>
                  {e.action}
                </span>
              </td>
              <td className="px-3 py-1.5">{e.by_name || e.by || '—'}</td>
              <td className="px-3 py-1.5 font-mono text-[10.5px] text-zinc-600">
                {Object.entries(e).filter(([k]) => !['at', 'action', 'by', 'by_name'].includes(k))
                  .map(([k, v]) => (
                    <span key={k} className="mr-2">
                      <span className="text-zinc-400">{k}=</span>
                      <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                    </span>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
